import trustedKeysDoc from './trusted_keys.json';

const REQUIRED_FIELDS = [
  'channel',
  'version',
  'artifactUrl',
  'artifactSha256',
  'manifestSha256',
  'signature',
  'keyId',
  'publishedAt',
  'rollbackVersion'
];

const FORBIDDEN_FLOATING_VERSIONS = new Set(['latest', '*', 'main', 'canary', 'stable']);
const SEMVER_RE = /^(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?(?:\+([0-9A-Za-z.-]+))?$/;

function stableStringify(value) {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  }
  const keys = Object.keys(value).sort();
  const body = keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',');
  return `{${body}}`;
}

function payloadFromManifest(manifest) {
  return {
    channel: String(manifest.channel || '').trim(),
    version: String(manifest.version || '').trim(),
    artifactUrl: String(manifest.artifactUrl || '').trim(),
    artifactSha256: String(manifest.artifactSha256 || '').trim().toLowerCase(),
    keyId: String(manifest.keyId || '').trim(),
    publishedAt: String(manifest.publishedAt || '').trim(),
    rollbackVersion: String(manifest.rollbackVersion || '').trim()
  };
}

function normalizeTrustedKeys() {
  const keys = Array.isArray(trustedKeysDoc && trustedKeysDoc.keys) ? trustedKeysDoc.keys : [];
  const map = new Map();
  for (const item of keys) {
    const keyId = String(item && item.keyId ? item.keyId : '').trim();
    const publicKeyPem = String(item && item.publicKeyPem ? item.publicKeyPem : '').trim();
    if (!keyId || !publicKeyPem) continue;
    map.set(keyId, { keyId, publicKeyPem });
  }
  return map;
}

function bytesFromBase64(value) {
  const text = String(value || '').trim();
  if (!text) return new Uint8Array();
  const binary = atob(text);
  const out = new Uint8Array(binary.length);
  for (let idx = 0; idx < binary.length; idx += 1) out[idx] = binary.charCodeAt(idx);
  return out;
}

function bytesFromPem(pemText) {
  const clean = String(pemText || '')
    .replace(/-----BEGIN [^-]+-----/g, '')
    .replace(/-----END [^-]+-----/g, '')
    .replace(/\s+/g, '');
  return bytesFromBase64(clean);
}

function hexFromBytes(bytes) {
  return Array.from(bytes).map((value) => value.toString(16).padStart(2, '0')).join('');
}

async function sha256Hex(input) {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return hexFromBytes(new Uint8Array(digest));
}

function ensureRequiredManifestFields(manifest) {
  for (const field of REQUIRED_FIELDS) {
    const value = String(manifest && manifest[field] ? manifest[field] : '').trim();
    if (!value) {
      throw Object.assign(new Error(`Missing channel manifest field: ${field}`), {
        code: 'CHANNEL_MANIFEST_FIELD_MISSING',
        field
      });
    }
  }
}

function parseSemver(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  const match = text.match(SEMVER_RE);
  if (!match) return null;
  return {
    major: Number(match[1]),
    minor: Number(match[2]),
    patch: Number(match[3]),
    prerelease: match[4] || ''
  };
}

function comparePrerelease(left = '', right = '') {
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  const leftParts = left.split('.');
  const rightParts = right.split('.');
  const length = Math.max(leftParts.length, rightParts.length);
  for (let idx = 0; idx < length; idx += 1) {
    const a = leftParts[idx];
    const b = rightParts[idx];
    if (a === undefined) return -1;
    if (b === undefined) return 1;
    const aNum = /^\d+$/.test(a) ? Number(a) : NaN;
    const bNum = /^\d+$/.test(b) ? Number(b) : NaN;
    const aIsNum = Number.isFinite(aNum);
    const bIsNum = Number.isFinite(bNum);
    if (aIsNum && bIsNum) {
      if (aNum !== bNum) return aNum > bNum ? 1 : -1;
      continue;
    }
    if (aIsNum !== bIsNum) return aIsNum ? -1 : 1;
    if (a !== b) return a > b ? 1 : -1;
  }
  return 0;
}

function compareSemver(leftValue, rightValue) {
  const left = parseSemver(leftValue);
  const right = parseSemver(rightValue);
  if (!left || !right) return NaN;
  if (left.major !== right.major) return left.major > right.major ? 1 : -1;
  if (left.minor !== right.minor) return left.minor > right.minor ? 1 : -1;
  if (left.patch !== right.patch) return left.patch > right.patch ? 1 : -1;
  return comparePrerelease(left.prerelease, right.prerelease);
}

async function verifyManifestSignatureAndHash(manifest, trustedKeys) {
  ensureRequiredManifestFields(manifest);
  const payload = payloadFromManifest(manifest);
  const payloadText = stableStringify(payload);
  const payloadBytes = new TextEncoder().encode(payloadText);

  const expectedManifestSha = await sha256Hex(payloadBytes);
  const actualManifestSha = String(manifest.manifestSha256 || '').trim().toLowerCase();
  if (actualManifestSha !== expectedManifestSha) {
    throw Object.assign(new Error('Channel manifest hash mismatch.'), {
      code: 'CHANNEL_MANIFEST_HASH_MISMATCH'
    });
  }

  const trusted = trustedKeys.get(payload.keyId);
  if (!trusted) {
    throw Object.assign(new Error(`Trusted key not found for keyId ${payload.keyId}.`), {
      code: 'CHANNEL_TRUSTED_KEY_MISSING'
    });
  }

  const keyBytes = bytesFromPem(trusted.publicKeyPem);
  const cryptoKey = await crypto.subtle.importKey(
    'spki',
    keyBytes,
    { name: 'Ed25519' },
    false,
    ['verify']
  );

  const signatureBytes = bytesFromBase64(manifest.signature);
  const validSignature = await crypto.subtle.verify(
    'Ed25519',
    cryptoKey,
    signatureBytes,
    payloadBytes
  );

  if (!validSignature) {
    throw Object.assign(new Error('Channel manifest signature is invalid.'), {
      code: 'CHANNEL_SIGNATURE_INVALID'
    });
  }
}

function normalizeRuntimeConfig(appManifest, runtimeEnv) {
  const appCore = appManifest && typeof appManifest.platformCore === 'object' && appManifest.platformCore
    ? appManifest.platformCore
    : {};
  const envCore = runtimeEnv && typeof runtimeEnv.platformCore === 'object' && runtimeEnv.platformCore
    ? runtimeEnv.platformCore
    : {};

  const mode = String(envCore.mode || appCore.mode || 'workspace').trim().toLowerCase();
  const channel = String(envCore.channel || appCore.channel || '').trim();
  const version = String(envCore.version || appCore.version || '').trim();
  const minVersion = String(envCore.minVersion || appCore.minVersion || '').trim();
  const manifestUrl = String(envCore.manifestUrl || appCore.manifestUrl || '').trim();
  const rollbackVersion = String(envCore.rollbackVersion || appCore.rollbackVersion || '').trim();

  return {
    mode: mode === 'channel' ? 'channel' : 'workspace',
    channel,
    version,
    minVersion,
    manifestUrl,
    rollbackVersion
  };
}

function ensureChannelPins(config) {
  if (!config.channel) {
    throw Object.assign(new Error('platformCore.channel is required when mode=channel.'), {
      code: 'CHANNEL_MODE_CHANNEL_REQUIRED'
    });
  }
  if (!config.version) {
    throw Object.assign(new Error('platformCore.version is required when mode=channel.'), {
      code: 'CHANNEL_MODE_VERSION_REQUIRED'
    });
  }
  if (FORBIDDEN_FLOATING_VERSIONS.has(config.version.toLowerCase())) {
    throw Object.assign(new Error(`Floating version '${config.version}' is forbidden in channel mode.`), {
      code: 'CHANNEL_MODE_FLOATING_VERSION_FORBIDDEN'
    });
  }
  if (!config.minVersion) {
    throw Object.assign(new Error('platformCore.minVersion is required when mode=channel.'), {
      code: 'CHANNEL_MODE_MIN_VERSION_REQUIRED'
    });
  }
  if (!parseSemver(config.version)) {
    throw Object.assign(new Error(`platformCore.version must be SemVer: ${config.version}`), {
      code: 'CHANNEL_MODE_VERSION_INVALID'
    });
  }
  if (!parseSemver(config.minVersion)) {
    throw Object.assign(new Error(`platformCore.minVersion must be SemVer: ${config.minVersion}`), {
      code: 'CHANNEL_MODE_MIN_VERSION_INVALID'
    });
  }
  if (compareSemver(config.version, config.minVersion) < 0) {
    throw Object.assign(new Error(`platformCore.version ${config.version} is lower than minVersion ${config.minVersion}.`), {
      code: 'CHANNEL_MODE_MIN_VERSION_VIOLATION'
    });
  }
  if (!config.manifestUrl) {
    throw Object.assign(new Error('platformCore.manifestUrl is required when mode=channel.'), {
      code: 'CHANNEL_MODE_MANIFEST_URL_REQUIRED'
    });
  }
}

function readRuntimeEnv() {
  if (typeof window === 'undefined') return {};
  return window.__KG_PLATFORM_CORE_RUNTIME__ && typeof window.__KG_PLATFORM_CORE_RUNTIME__ === 'object'
    ? window.__KG_PLATFORM_CORE_RUNTIME__
    : {};
}

export async function loadBootManifestAppFromChannel({
  appManifest = {},
  fallbackBootManifestApp,
  runtimeEnv = readRuntimeEnv()
} = {}) {
  if (typeof fallbackBootManifestApp !== 'function') {
    throw new Error('fallbackBootManifestApp is required.');
  }

  const config = normalizeRuntimeConfig(appManifest, runtimeEnv);
  if (config.mode !== 'channel') {
    return fallbackBootManifestApp;
  }

  ensureChannelPins(config);
  if (!crypto || !crypto.subtle) {
    throw Object.assign(new Error('Web Crypto API is required for channel verification.'), {
      code: 'CHANNEL_CRYPTO_UNAVAILABLE'
    });
  }

  const trustedKeys = normalizeTrustedKeys();
  if (!trustedKeys.size) {
    throw Object.assign(new Error('No trusted keys configured for platform-core channel verification.'), {
      code: 'CHANNEL_TRUST_STORE_EMPTY'
    });
  }

  const manifestAbsoluteUrl = new URL(config.manifestUrl, window.location.href).toString();
  const manifestResponse = await fetch(manifestAbsoluteUrl, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'same-origin'
  });
  if (!manifestResponse.ok) {
    throw Object.assign(new Error(`Channel manifest fetch failed (${manifestResponse.status}).`), {
      code: 'CHANNEL_MANIFEST_FETCH_FAILED'
    });
  }
  const manifest = await manifestResponse.json();
  await verifyManifestSignatureAndHash(manifest, trustedKeys);

  if (String(manifest.channel || '').trim() !== config.channel) {
    throw Object.assign(new Error(`Channel mismatch: requested ${config.channel}, got ${manifest.channel || '(empty)'}.`), {
      code: 'CHANNEL_MANIFEST_CHANNEL_MISMATCH'
    });
  }
  if (String(manifest.version || '').trim() !== config.version) {
    throw Object.assign(new Error(`Version mismatch: requested ${config.version}, got ${manifest.version || '(empty)'}.`), {
      code: 'CHANNEL_MANIFEST_VERSION_MISMATCH'
    });
  }
  if (!parseSemver(String(manifest.version || '').trim())) {
    throw Object.assign(new Error(`Channel manifest version must be SemVer: ${manifest.version || '(empty)'}.`), {
      code: 'CHANNEL_MANIFEST_VERSION_INVALID'
    });
  }
  if (compareSemver(String(manifest.version || '').trim(), config.minVersion) < 0) {
    throw Object.assign(new Error(`Channel manifest version ${manifest.version || '(empty)'} is lower than app minVersion ${config.minVersion}.`), {
      code: 'CHANNEL_MANIFEST_MIN_VERSION_VIOLATION'
    });
  }
  if (config.rollbackVersion && String(manifest.rollbackVersion || '').trim() !== config.rollbackVersion) {
    throw Object.assign(new Error(`Rollback version mismatch: requested ${config.rollbackVersion}, got ${manifest.rollbackVersion || '(empty)'}.`), {
      code: 'CHANNEL_MANIFEST_ROLLBACK_MISMATCH'
    });
  }

  const artifactAbsoluteUrl = new URL(String(manifest.artifactUrl || '').trim(), manifestAbsoluteUrl).toString();
  const artifactResponse = await fetch(artifactAbsoluteUrl, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'same-origin'
  });
  if (!artifactResponse.ok) {
    throw Object.assign(new Error(`Core artifact fetch failed (${artifactResponse.status}).`), {
      code: 'CHANNEL_ARTIFACT_FETCH_FAILED'
    });
  }

  const artifactBytes = new Uint8Array(await artifactResponse.arrayBuffer());
  const artifactSha = await sha256Hex(artifactBytes);
  const expectedArtifactSha = String(manifest.artifactSha256 || '').trim().toLowerCase();
  if (artifactSha !== expectedArtifactSha) {
    throw Object.assign(new Error('Core artifact hash mismatch.'), {
      code: 'CHANNEL_ARTIFACT_HASH_MISMATCH'
    });
  }

  const moduleBlob = new Blob([artifactBytes], { type: 'text/javascript' });
  const blobUrl = URL.createObjectURL(moduleBlob);
  try {
    const module = await import(blobUrl);
    if (typeof module.bootManifestApp !== 'function') {
      throw Object.assign(new Error('Signed core artifact must export bootManifestApp().'), {
        code: 'CHANNEL_ARTIFACT_EXPORT_MISSING'
      });
    }
    return module.bootManifestApp;
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}
